import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ProgressBar } from 'react-bootstrap';
import { stopAutomixBuild } from '../../redux/thunk/automixThunk';
import { formatClock } from '../../utils/automixFormat';
import { DEFAULT_PLAYLIST_COVER } from '../../utils/cover';

// What each engine stage is doing, for the line under the active stage.
const STAGE_NOTES = {
    stems: 'Locating vocals, bass, drums and melody',
    analyze: 'Tempo, beat grid, key, phrases, energy',
    plan: 'Scoring every pair of tracks and choosing how each one hands over',
    stretch: 'Putting every track on one mix clock',
    sync: 'Measuring every overlap, kick against kick',
    render: 'EQ, filters and faders, track by track',
    master: 'Loudness and the final file',
};

const StageIcon = ({ status }) => {
    if (status === 'done') {
        return (
            <svg className="amx-stage-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M5 12.5l4.5 4.5L19 7.5" />
            </svg>
        );
    }
    return <span className="amx-stage-dot" aria-hidden="true" />;
};

const AutomixBuild = () => {
    const dispatch = useDispatch();
    const { source, progress, phase, setName, startedAt } = useSelector((s) => s.automix);
    const stopping = phase === 'stopping';

    // Elapsed time ticks locally between engine events (it is a clock, not progress).
    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(id);
    }, []);

    const stages = progress?.stages || [];
    const percent = progress?.percent || 0;

    return (
        <div className="amx-build">
            <div className="amx-build-card">
                <header className="amx-build-head">
                    <img className="amx-build-cover" src={source?.cover || DEFAULT_PLAYLIST_COVER} alt="" />
                    <div className="amx-build-title">
                        <span className="amx-eyebrow">{stopping ? 'Stopping' : 'Building your set'}</span>
                        <h2 title={progress?.name || setName}>{progress?.name || setName || source?.name}</h2>
                    </div>
                    <span className="amx-build-clock" aria-label="Elapsed time">{formatClock(startedAt ? Math.max(0, now - startedAt) / 1000 : 0)}</span>
                </header>

                <div className="amx-build-bar">
                    <ProgressBar now={percent} className="custom-progress-bar" />
                    <span className="amx-build-percent">{Math.floor(percent)}%</span>
                </div>

                <ol className="amx-stages">
                    {stages.map((s) => (
                        <li key={s.id} className={`amx-stage is-${s.status}`}>
                            <StageIcon status={s.status} />
                            <div className="amx-stage-text">
                                <span className="amx-stage-label">{s.label}</span>
                                {s.status === 'active' && (
                                    <span className="amx-stage-note">
                                        {progress?.detail || STAGE_NOTES[s.id]}
                                    </span>
                                )}
                            </div>
                            {s.total > 1 && s.status !== 'pending' && (
                                <span className="amx-stage-count">{Math.min(s.done, s.total)}/{s.total}</span>
                            )}
                        </li>
                    ))}
                </ol>

                {(progress?.warnings || []).length > 0 && (
                    <p className="amx-build-warning">
                        {progress.warnings.length === 1 ? 'One pair' : `${progress.warnings.length} pairs`} couldn’t be blended cleanly and will be cut instead.
                    </p>
                )}

                <footer className="amx-build-foot">
                    <p>You can keep using MixGoat — the set keeps building, and we’ll tell you when it’s ready.</p>
                    <button type="button" className="amx-stop-btn" disabled={stopping} onClick={() => dispatch(stopAutomixBuild())}>
                        {stopping ? 'Stopping…' : 'Stop'}
                    </button>
                </footer>
            </div>
        </div>
    );
};

export default AutomixBuild;
